import React, { useEffect, useState } from "react";

// icons
import {
  MdSearch,
  MdAddCircleOutline,
  MdVisibility,
  MdArrowUpward,
  MdArrowDownward,
} from "react-icons/md";
import Card from "../../../../components/card";
import ModalAddClient from "./ModalAddClient";
import ModalViewDetailClient from "./ModalViewDetailClient";
import SkeletonTable from "../../../../components/skeleton/table";
import api from "../../../../services/axios";
import { getJiraProjects } from "../../../../services/jiraProjects";
import { toast } from "react-toastify";

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface ResponsiveTableProps {
  columns: TableColumn[];
  data: TableRow[];
}

const ClientManagementTable: React.FC<ResponsiveTableProps> = ({
  columns,
  data,
}) => {
  const [searchTerm, setSearchTerm] = useState<any>("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [openAddClientModal, setOpenAddClientModal] = useState<boolean>(false);
  const [openClientDetailModal, setClientDetailModal] =
    useState<boolean>(false);
  const [clientDetail, setClientDetail] = useState<object | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [clientDataTable, setClientDataTable] = useState<TableRow[]>(data);
  const [jiraProjectList, setJiraProjectList] = useState<[]>([])
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Ativa/inativa o cliente direto na lista (sem abrir o modal). Atualização otimista com
  // rollback em erro. Usa o endpoint parcial PATCH (não exige o objeto inteiro do cliente).
  const handleToggleActive = async (row: TableRow) => {
    const id = Number(row.id);
    const current = Boolean(row.flActive);
    const next = !current;

    setTogglingId(id);
    setClientDataTable((prev) =>
      prev.map((r) =>
        Number(r.id) === id ? ({ ...r, flActive: next } as unknown as TableRow) : r
      )
    );

    try {
      await api.patch(`/Client/Clients/${id}/active`, { flActive: next });
      toast.success(next ? "Cliente ativado" : "Cliente inativado");
    } catch (error) {
      console.error("Erro ao atualizar status do cliente:", error);
      setClientDataTable((prev) =>
        prev.map((r) =>
          Number(r.id) === id ? ({ ...r, flActive: current } as unknown as TableRow) : r
        )
      );
      toast.error("Erro ao atualizar status");
    } finally {
      setTogglingId(null);
    }
  };

  const fetchJiraProjects = async () => {
    const projects = await getJiraProjects()
    setJiraProjectList(projects)
  }

  useEffect(() => {
    fetchJiraProjects()
  }, []);

  const handleSort = (columnKey: string) => {
    setSortedColumn(columnKey);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredData = clientDataTable.filter((row) =>
    Object.values(row).some((value) =>
      value
        ? value.toString().toLowerCase().includes(searchTerm?.toLowerCase())
        : ""
    )
  );

  // Ativos sempre primeiro; dentro de cada grupo aplica a ordenação por coluna (ou ordem natural).
  const sortedData = [...filteredData].sort((a, b) => {
    const byActive = Number(Boolean(b.flActive)) - Number(Boolean(a.flActive));
    if (byActive !== 0) return byActive;
    if (!sortedColumn) return 0;
    return sortOrder === "asc"
      ? a[sortedColumn] > b[sortedColumn]
        ? 1
        : -1
      : a[sortedColumn] < b[sortedColumn]
      ? 1
      : -1;
  });

  const handleRowClick = (row: TableRow) => {
    setClientDetail(row);
    setClientDetailModal(true);
  };

  const handleOpenModal = () => {
    setOpenAddClientModal(true);
  };

  const getDetails = async () => {
    setLoading(true);
    function fetchClients() {
      api
        .get("/Client/Clients")
        .then((response) => {
          setClientDataTable(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("There was an error!", error);
          setLoading(true);
        });
    }
    fetchClients();
  };

  return (
    <div>
      {loading ? (
        <SkeletonTable active={loading} />
      ) : (
        <div className="animate-fade-up">
          <Card extra="p-5">
            {/* Toolbar */}
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              {/* Search */}
              <div className="flex flex-1 flex-col gap-1.5">
              <label
                htmlFor="client-search"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                Buscar
              </label>
              <div className="relative">
                <MdSearch
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
                <input
                  id="client-search"
                  type="text"
                  placeholder="Buscar por nome, CNPJ ou e-mail…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
              </div>
              <button
                type="button"
                onClick={handleOpenModal}
                className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
              >
                <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
                Adicionar cliente
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-navy-900">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className="flex items-center gap-1 uppercase transition-colors hover:text-dv-blue-600 dark:hover:text-dv-cyan-300"
                        >
                          {column.header}
                          {sortedColumn === column.key &&
                            (sortOrder === "asc" ? (
                              <MdArrowUpward className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <MdArrowDownward className="h-3.5 w-3.5" aria-hidden="true" />
                            ))}
                        </button>
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 2}
                        className="px-4 py-12 text-center text-sm text-gray-700 dark:text-gray-300"
                      >
                        Nenhum cliente encontrado.
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                      >
                        {columns.map((column, colIndex) => (
                          <td
                            key={column.key}
                            className={`px-4 py-3 ${
                              colIndex === 0
                                ? "font-semibold text-navy-700 dark:text-white"
                                : "text-gray-700 dark:text-gray-300"
                            } ${column.key === "document" ? "tabular-nums" : ""}`}
                          >
                            <span
                              className="block max-w-[220px] truncate"
                              title={String(row[column.key] ?? "")}
                            >
                              {row[column.key]}
                            </span>
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            disabled={togglingId === Number(row.id)}
                            aria-label={
                              Boolean(row.flActive) ? "Inativar cliente" : "Ativar cliente"
                            }
                            title={
                              Boolean(row.flActive)
                                ? "Ativo — clique para inativar"
                                : "Inativo — clique para ativar"
                            }
                            onClick={() => handleToggleActive(row)}
                            className="inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span
                              aria-hidden="true"
                              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                                Boolean(row.flActive)
                                  ? "bg-emerald-400"
                                  : "bg-gray-300 dark:bg-navy-600"
                              }`}
                            >
                              <span
                                className={`absolute h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                  Boolean(row.flActive) ? "translate-x-5" : "translate-x-0.5"
                                }`}
                              />
                            </span>
                            <span
                              className={`text-xs font-semibold ${
                                Boolean(row.flActive)
                                  ? "text-emerald-600 dark:text-emerald-300"
                                  : "text-gray-700 dark:text-gray-300"
                              }`}
                            >
                              {Boolean(row.flActive) ? "Ativo" : "Inativo"}
                            </span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            aria-label={`Ver detalhes de ${row[columns[0].key]}`}
                            title="Ver detalhes"
                            onClick={() => handleRowClick(row)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:border-dv-blue-500 hover:text-dv-blue-600 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:text-dv-cyan-300"
                          >
                            <MdVisibility className="h-4 w-4" aria-hidden="true" />
                            <span className="hidden sm:inline">Detalhes</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
      <ModalAddClient
        openAddClientModal={openAddClientModal}
        setOpenAddClientModal={setOpenAddClientModal}
        updateState={() => getDetails()}
        jiraProjectList={jiraProjectList}
      ></ModalAddClient>
      <ModalViewDetailClient
        openClientDetailModal={openClientDetailModal}
        setOpenClientDetailModal={setClientDetailModal}
        details={clientDetail}
        updateState={() => getDetails()}
        jiraProjectList={jiraProjectList}
      ></ModalViewDetailClient>
    </div>
  );
};

export default ClientManagementTable;
